import React, { useMemo } from 'react'
import { useStyles } from './DetailedFreeTrial.styles'
import { dateAndTimeConversion } from '../../../common/utils/dateAndTimeConversion'
import { useSelector } from '../../../store/hooks'
import { Typography } from '@mui/material'
import { Product } from '../../../modules/productsListing/types'
import {
  FreeTrials,
  ProductDimensionFreeTrial,
} from '../../../modules/freeTrials/types'
import { isEmpty } from 'lodash'
import { CustomEulaDiv } from '../../PrivateOfferForm/DetailedOffer/DetailedOffer'
import { detailedFreeTrialHeading } from './DetailedFreeTrial.constants'

export type DetailedFreeTrialProps = {
  productFreeTrial: FreeTrials
  product: Product
}

export const DetailedFreeTrial: React.FC<DetailedFreeTrialProps> = ({
  productFreeTrial,
  product,
}) => {
  const timeZone = useSelector<string>(
    state =>
      state.userProfile.userProfile.timeZone ||
      state.userProfile.userProfile.defaultTimeZone ||
      'UTC'
  )

  const { classes } = useStyles()
  const { productDimensions, freeTrial, eulaType, eulaFile } = productFreeTrial
  const { modifiedDate } = dateAndTimeConversion(
    freeTrial?.freeTrialEnabledAt || new Date(),
    timeZone
  )

  return (
    <div
      data-testid="free-trial-submission-ui"
      className={classes.submissionContainer}
    >
      <div className={classes.dimensionsContainer}>
        <div className={classes.dimensionContainer}>
          <Typography className={classes.dimensionsHeading}>
            {detailedFreeTrialHeading.productDimensions}
          </Typography>
          <Typography className={classes.dimensionsHeading}>
            {detailedFreeTrialHeading.freeTrialQuantity}
          </Typography>
        </div>
        {productDimensions.map(dimension => (
          <RenderDimension productDimension={dimension} product={product} />
        ))}
      </div>
      <div className={classes.eulaContainer}>
        <Typography className={classes.eulaHeading}>
          {detailedFreeTrialHeading.eula}
        </Typography>
        {!isEmpty(eulaFile) ? (
          <div
            className={classes.customEula}
            data-testid="custom-eula-container"
          >
            <CustomEulaDiv eulaFiles={eulaFile || []} label={eulaType || ''} />
          </div>
        ) : (
          <Typography data-testid="eula-data" className={classes.eulaText}>
            {eulaType}
          </Typography>
        )}
      </div>
      <div className={classes.dateContainer}>
        {!isEmpty(freeTrial?.freeTrialEnabledAt) ? (
          <div className={classes.dateText}>
            <Typography className={classes.dateHeading}>
              {detailedFreeTrialHeading.createdAt}
            </Typography>
            <Typography data-testid="free-trial-enabled-at">
              {modifiedDate}
            </Typography>
          </div>
        ) : null}
        <div className={classes.dateText}>
          <Typography className={classes.dateHeading}>
            {detailedFreeTrialHeading.freeTrialExpiresInDays}
          </Typography>
          <Typography data-testid="free-trial-expires-in-days">
            {freeTrial?.freeTrialExpiresInDays || 0} Day(s)
          </Typography>
        </div>
      </div>
    </div>
  )
}
export type RenderDimensionProps = {
  productDimension: ProductDimensionFreeTrial
  product: Product
}
export const RenderDimension = React.memo(
  ({ productDimension, product }: RenderDimensionProps) => {
    const { classes } = useStyles()
    const { productDimensionId, freeTrial } = productDimension
    const name = useMemo(() => {
      return (
        product?.productDimensions?.find(
          dimension => dimension.id === productDimension.productDimensionId
        )?.name || ''
      )
    }, [productDimension, product])
    return (
      <div className={classes.dimensionContainer}>
        <Typography
          className={classes.dimensionText}
          data-testid={`${productDimensionId}-name`}
        >
          {name}
        </Typography>
        <Typography
          className={classes.dimensionText}
          data-testid={`${productDimensionId}-qty`}
        >
          {freeTrial?.quantity}
        </Typography>
      </div>
    )
  }
)
